import type { ChangeKind, PlanChanges } from '@itin/shared'
import { changesSince, NO_PLAN_CHANGES } from '@itin/shared'
import { useEffect, useMemo, useState } from 'react'
import type { SharePlan } from '@itin/shared'
import { useT } from '../lib/i18n.js'

/**
 * "Three things changed since you last looked", and a way to go and see them.
 *
 * Ana edits a trip after the link has gone out. Until now the traveller opened
 * the same URL and got a silently different document — the flight moved forty
 * minutes and the page looked exactly as it had the day before. This is the
 * smallest thing that fixes that: a count, a way to reach the first one, and a
 * dismiss.
 *
 * **The baseline is `localStorage`, keyed by share token.** There is no
 * traveller account and there must not be one — possession of a link proves
 * possession of a URL, not identity (PATTERNS §6) — so the only place a
 * per-viewer "when did I last look" can live is the viewer's own browser. Keyed
 * by token rather than one global key because a family opening two links on one
 * phone are two different readers of two different documents.
 *
 * What that cannot do, stated here because the UI has to be honest about it:
 *
 *  - **A new device shows nothing as new.** A traveller who read the itinerary
 *    on a laptop and opens it on a phone has no baseline on the phone, so the
 *    phone reports no changes at all. Reporting everything instead would be
 *    worse: "47 things changed" on a nine-day trip is not information.
 *  - **A cleared browser is a new device.** Same answer, same reason.
 *  - **Removals are invisible.** See `changesSince` in `@itin/shared` for why
 *    telling the traveller would mean an audit log.
 *
 * The first two are what `updates.limits` says out loud under the headline.
 *
 * Storage is wrapped in try/catch throughout. Safari in private mode throws on
 * `setItem`, and a page that white-screens because it could not record that
 * somebody looked at it would be a spectacularly bad trade for this feature.
 * Failing means no baseline, which means no banner — the same state as a first
 * visit, and the itinerary itself is untouched.
 */

const STORAGE_PREFIX = 'itin.lastSeen.'

function storageKey(token: string): string {
  return `${STORAGE_PREFIX}${token}`
}

function readLastSeen(token: string): string | null {
  try {
    return localStorage.getItem(storageKey(token))
  } catch {
    return null
  }
}

function writeLastSeen(token: string, at: string): void {
  try {
    localStorage.setItem(storageKey(token), at)
  } catch {
    // Private mode, or a full quota. Nothing to do and nothing to say: the
    // itinerary is what the traveller came for.
  }
}

export interface PlanUpdatesState {
  changes: PlanChanges
  dismiss(): void
}

/**
 * Reads the baseline once, at mount, and returns what has changed since it.
 *
 * Two writes, and only two. The FIRST sight of a token records `readAt` and
 * reports nothing — you cannot have missed anything you have not yet been shown,
 * and a first visit that announced the whole itinerary as new would train
 * everybody to ignore the banner immediately. A DISMISS records `readAt` again,
 * which is what makes the banner survive a reload: recording it on every load
 * would mean a traveller who opened the link, scrolled, and came back an hour
 * later had lost the list of what to look at.
 */
export function usePlanUpdates(token: string, plan: SharePlan | undefined): PlanUpdatesState {
  // Read once. Re-reading on each render would clear the banner the instant the
  // baseline below is written, which is the bug this shape exists to avoid.
  const [seenAt, setSeenAt] = useState<string | null>(() => readLastSeen(token))

  const readAt = plan?.readAt

  useEffect(() => {
    if (!readAt) return
    if (readLastSeen(token) !== null) return
    writeLastSeen(token, readAt)
  }, [token, readAt])

  const changes = useMemo(
    () => (plan ? changesSince(plan, seenAt) : NO_PLAN_CHANGES),
    [plan, seenAt],
  )

  return {
    changes,
    dismiss() {
      if (!readAt) return
      writeLastSeen(token, readAt)
      setSeenAt(readAt)
    },
  }
}

/**
 * The banner.
 *
 * `no-print` because it is about a browser's memory of a page. On paper — the
 * copy that gets forwarded on WhatsApp and put in a folder — "3 things changed"
 * refers to nothing the reader can act on and dates the document.
 */
export function PlanUpdates({ changes, onDismiss }: { changes: PlanChanges; onDismiss(): void }) {
  const t = useT()
  if (changes.count === 0) return null

  return (
    <aside
      data-testid="plan-updates"
      className="no-print flex flex-wrap items-center gap-x-4 gap-y-3 rounded-lg border border-gold bg-[#fdf6e6] px-4 py-3"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-medium text-slate-900">
          {changes.count === 1
            ? t('updates.oneChanged')
            : t('updates.manyChanged', { count: String(changes.count) })}
        </span>
        <span className="text-xs leading-snug text-slate-500">{t('updates.limits')}</span>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {/*
          The first change in DOCUMENT order, found by the marker the entries
          carry rather than by working out which day index to scroll to. That
          keeps this correct for a changed day heading, a changed entry inside a
          day, and a change in the unscheduled section at the foot of the page —
          three cases that have three different anchors and one right answer.
        */}
        <button
          type="button"
          onClick={() =>
            document
              .querySelector('[data-changed]')
              ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
          className="flex min-h-11 items-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-800 transition-colors hover:border-gold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold sm:min-h-10"
        >
          {t('updates.show')}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="flex min-h-11 items-center rounded-md px-3 text-sm text-slate-600 transition-colors hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold sm:min-h-10"
        >
          {t('updates.dismiss')}
        </button>
      </div>
    </aside>
  )
}

/**
 * The marker beside a day heading or an entry title.
 *
 * `data-changed` is what the "show me" button above searches for, so this
 * component is also the anchor. Keeping the two together means a marker cannot
 * be added somewhere the jump does not know about.
 */
export function ChangeBadge({ kind }: { kind: ChangeKind }) {
  const t = useT()
  return (
    <span
      data-changed={kind}
      className="no-print rounded bg-gold/25 px-1.5 py-0.5 text-xs font-medium text-slate-800"
    >
      {kind === 'added' ? t('updates.added') : t('updates.edited')}
    </span>
  )
}
